import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardIcon from '@material-ui/icons/Dashboard';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import TuneIcon from '@material-ui/icons/Tune';
import RoomIcon from '@material-ui/icons/Room';
import ExtensionIcon from '@material-ui/icons/Extension';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

const API_BASE = '/planner-api';

// Cartões KPI — estilo Material Dashboard 2 (ícone em gradiente + número grande).
// Tons SUAVES — fundo levemente tingido + ícone na cor (sem gradientes fortes).
const KPIS = [
  { key: 'orgs',     page: 'clients',  label: 'Organizações', icon: BusinessIcon,  tint: '#eaf2fe', color: '#2b7de9', hint: 'abrir Organizações' },
  { key: 'users',    page: 'users',    label: 'Utilizadores', icon: PeopleIcon,    tint: '#e9f7ec', color: '#3a9f52', hint: 'abrir Utilizadores' },
  { key: 'profiles', page: 'profiles', label: 'Perfis',       icon: TuneIcon,      tint: '#eeeefc', color: '#5b57e0', hint: 'abrir Perfis' },
  { key: 'zones',    page: 'zones',    label: 'Zonas',        icon: RoomIcon,      tint: '#e6f7f6', color: '#12a89c', hint: 'abrir Zonas' },
  { key: 'modules',  page: 'modules',  label: 'Módulos',      icon: ExtensionIcon, tint: '#fff2e2', color: '#ef8b32', hint: 'abrir Módulos' },
];

const useClassicStyles = makeStyles(() => ({
  '@global': { '@keyframes upIn': { '0%': { opacity: 0, transform: 'translateY(16px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } } },
  header: { marginBottom: 22, display: 'flex', alignItems: 'center', gap: 14, background: 'var(--adm-header-bg, #e2f1e9)', border: '1px solid var(--adm-header-border, #bfe0d1)', borderRadius: 14, padding: '18px 22px' },
  headerIcon: { width: 44, height: 44, borderRadius: 12, background: 'var(--adm-brand-soft, rgba(47,93,80,0.10))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: '1.7rem', fontWeight: 800, color: '#344767', letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.85rem', color: '#67748e', marginTop: 3 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 20, marginBottom: 26 },
  kpi: { position: 'relative', background: '#fff', borderRadius: 16, padding: '16px 18px 18px', boxShadow: '0 4px 18px rgba(52,71,103,0.08)', display: 'flex', flexDirection: 'column', animation: '$upIn 0.4s ease-out both', cursor: 'pointer', border: '1px solid transparent', transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(52,71,103,0.14)', borderColor: '#d8e9e1' } },
  kpiTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  kpiIcon: { width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiNum: { fontSize: '1.9rem', fontWeight: 800, color: '#344767', lineHeight: 1, letterSpacing: '-0.03em' },
  kpiLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#67748e', textAlign: 'right' },
  kpiFoot: { borderTop: '1px solid #f0f2f5', marginTop: 14, paddingTop: 10, fontSize: '0.72rem', color: 'var(--adm-brand-mid, #5a8577)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 },

  // diagrama — cartão clicável (abre o ecrã "Como funciona o modelo")
  card: { background: '#fff', borderRadius: 16, boxShadow: '0 4px 18px rgba(52,71,103,0.08)', padding: '22px 26px 26px', marginBottom: 22, cursor: 'pointer', border: '1px solid transparent', transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 32px rgba(52,71,103,0.14)', borderColor: '#d8e9e1' } },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  cardTitle: { fontSize: '1.05rem', fontWeight: 800, color: '#344767', flex: 1 },
  cardOpen: { display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', fontWeight: 700, color: 'var(--adm-brand, #2f5d50)', background: 'var(--adm-brand-soft, #eef5f1)', border: '1px solid #d8e9e1', borderRadius: 999, padding: '5px 12px' },
  cardSub: { fontSize: '0.78rem', color: '#67748e', marginBottom: 22 },
  flowRow: { display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap' },
  node: { borderRadius: 14, padding: '14px 16px', minWidth: 150, position: 'relative', border: '1px solid', boxShadow: '0 2px 8px rgba(52,71,103,0.05)' },
  nodeName: { fontSize: '0.98rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 },
  nodeSub: { fontSize: '0.68rem', fontWeight: 600, color: '#8a94a6', marginTop: 2 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 9 },
  chip: { fontSize: '0.64rem', fontWeight: 700, background: 'rgba(52,71,103,0.05)', color: '#67748e', padding: '3px 8px', borderRadius: 6 },
  conn: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 118, padding: '0 6px' },
  connLabel: { fontSize: '0.64rem', fontWeight: 700, color: '#67748e', textAlign: 'center', marginBottom: 3, lineHeight: 1.3 },
  connLine: { display: 'flex', alignItems: 'center', color: '#cbd5e1' },
  vconn: { display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '4px 0 4px 74px' },
  vLabel: { fontSize: '0.64rem', fontWeight: 700, color: '#67748e', marginBottom: 2 },
  note: { marginTop: 20, background: 'var(--adm-brand-soft, #eef5f1)', border: '1px solid #d8e9e1', borderRadius: 12, padding: '12px 16px', fontSize: '0.8rem', color: '#3f6b5c', fontWeight: 500, lineHeight: 1.5 },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 },
}));

// ─── Folha FuelCheck do Dashboard (REDESIGN, não recolor) ──────────────────
// Cabeçalho arejado sem caixa tingida; cartões KPI planos com NÚMERO grande,
// micro-rótulo em maiúsculas e um chip de ícone discreto; acento azul #0677A6.
const D_FONT = '"Inter","Inter Variable",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';
const D_LIGHT = {
  font: D_FONT, card: '#ffffff', ink: '#0b1a2c', tx3: '#5e7189', accent: '#0677a6', cyan: '#0796cc',
  line: 'rgba(22,58,96,0.10)', lineSoft: 'rgba(22,58,96,0.07)', hoverBorder: 'rgba(6,119,166,0.35)',
  shadow: '0 1px 2px rgba(11,26,44,0.04)', hoverShadow: '0 14px 30px rgba(11,26,44,0.10)',
  chipBg: 'rgba(6,119,166,0.08)', chipBorder: 'rgba(6,119,166,0.22)', logoShadow: '0 8px 18px rgba(6,119,166,0.22)',
};
const D_DARK = {
  font: D_FONT, card: '#14273d', ink: '#e7eef6', tx3: '#93a7be', accent: '#20a6d4', cyan: '#2bb8d6',
  line: 'rgba(146,175,205,0.16)', lineSoft: 'rgba(146,175,205,0.10)', hoverBorder: 'rgba(32,166,212,0.45)',
  shadow: '0 1px 2px rgba(0,0,0,0.35)', hoverShadow: '0 14px 30px rgba(0,0,0,0.5)',
  chipBg: 'rgba(32,166,212,0.14)', chipBorder: 'rgba(32,166,212,0.30)', logoShadow: '0 8px 18px rgba(32,166,212,0.30)',
};
const dashStyle = (D) => ({
  header: { marginBottom: 28, display: 'flex', alignItems: 'center', gap: 16, background: 'transparent', border: 'none', borderRadius: 0, padding: 0 },
  headerIcon: { width: 46, height: 46, borderRadius: 13, background: `linear-gradient(135deg, ${D.accent}, ${D.cyan})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: D.logoShadow },
  title: { fontSize: '1.8rem', fontWeight: 800, color: D.ink, letterSpacing: '-0.03em', fontFamily: D.font },
  subtitle: { fontSize: '0.86rem', color: D.tx3, marginTop: 3, fontFamily: D.font },
  // Saudação grande e amigável (feel da referência)
  greetWrap: { marginBottom: 34 },
  greetPill: { display: 'inline-flex', alignItems: 'center', gap: 8, height: 28, padding: '0 13px', borderRadius: 999, background: D.chipBg, border: `1px solid ${D.chipBorder}`, fontSize: '0.72rem', fontWeight: 700, color: D.accent, fontFamily: D.font, marginBottom: 16 },
  greetDot: { width: 7, height: 7, borderRadius: '50%', background: '#0fa06c', boxShadow: '0 0 8px rgba(15,160,108,0.7)' },
  greetTitle: { fontSize: '2.7rem', fontWeight: 700, color: D.ink, letterSpacing: '-0.035em', lineHeight: 1.08, fontFamily: D.font, margin: 0 },
  greetName: { background: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' },
  greetSub: { fontSize: '1.02rem', color: D.tx3, marginTop: 12, fontFamily: D.font, lineHeight: 1.5, maxWidth: 640 },
  greetStrong: { color: D.ink, fontWeight: 700 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 20, marginBottom: 32 },
  kpi: {
    position: 'relative', background: D.card, borderRadius: 20, padding: '24px 26px 22px',
    border: `1px solid ${D.line}`, boxShadow: D.shadow,
    display: 'flex', flexDirection: 'column', animation: '$upIn 0.4s ease-out both', cursor: 'pointer',
    transition: 'transform 0.22s var(--adm-ease, cubic-bezier(0.16,1,0.3,1)), box-shadow 0.22s ease, border-color 0.22s ease', fontFamily: D.font,
    '&:hover': { transform: 'translateY(-4px)', boxShadow: D.hoverShadow, borderColor: D.hoverBorder },
  },
  kpiTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  kpiIcon: { width: 40, height: 40, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiNum: { fontSize: '2.7rem', fontWeight: 800, color: D.ink, lineHeight: 1, letterSpacing: '-0.04em', fontFamily: D.font },
  kpiLabel: { fontSize: '0.64rem', fontWeight: 700, color: D.tx3, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: D.font },
  kpiFoot: { borderTop: `1px solid ${D.lineSoft}`, marginTop: 14, paddingTop: 11, fontSize: '0.7rem', color: D.accent, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: D.font },
  card: {
    background: D.card, borderRadius: 18, border: `1px solid ${D.line}`, boxShadow: D.shadow,
    padding: '22px 26px', marginBottom: 22, cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease', fontFamily: D.font,
    '&:hover': { transform: 'translateY(-3px)', boxShadow: D.hoverShadow, borderColor: D.hoverBorder },
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 },
  cardTitle: { fontSize: '1.05rem', fontWeight: 800, color: D.ink, flex: 1, fontFamily: D.font },
  cardOpen: { display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', fontWeight: 800, color: D.accent, background: D.chipBg, border: `1px solid ${D.chipBorder}`, borderRadius: 999, padding: '6px 13px', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: D.font },
  cardSub: { fontSize: '0.8rem', color: D.tx3, marginBottom: 0, fontFamily: D.font },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 },
});
const useFuelStyles = makeStyles(dashStyle(D_LIGHT));
const useFuelDarkStyles = makeStyles(dashStyle(D_DARK));

function AdminDashboard({ token, onNavigate, uiTheme, user }) {
  const go = (p) => onNavigate && onNavigate(p);
  const hour = new Date().getHours();
  const greet = (hour >= 6 && hour < 13) ? 'Bom dia' : (hour >= 13 && hour < 20) ? 'Boa tarde' : 'Boa noite';
  const firstName = ((user && user.name) || '').trim().split(/\s+/)[0] || 'Admin';
  const fc = uiTheme === 'fuelcheck' || uiTheme === 'fuelcheck-dark';
  const dark = uiTheme === 'fuelcheck-dark';
  const classicClasses = useClassicStyles();
  const fuelLightClasses = useFuelStyles();
  const fuelDarkClasses = useFuelDarkStyles();
  const classes = fc ? { ...classicClasses, ...(dark ? fuelDarkClasses : fuelLightClasses) } : classicClasses;
  const [counts, setCounts] = useState({ orgs: 0, users: 0, profiles: 0, zones: 0, modules: 0 });
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [c, u, p, z, m] = await Promise.all([
          fetch(`${API_BASE}/clients`, { headers }),
          fetch(`${API_BASE}/users`, { headers }),
          fetch(`${API_BASE}/profiles`, { headers }),
          fetch(`${API_BASE}/zones`, { headers }),
          fetch(`${API_BASE}/modules`, { headers }),
        ]);
        const len = async (r) => (r.ok ? (await r.json()).length : 0);
        setCounts({ orgs: await len(c), users: await len(u), profiles: await len(p), zones: await len(z), modules: await len(m) });
      } catch (e) { console.error('Dashboard load error:', e); }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  if (loading) return <Box className={classes.loading}><CircularProgress style={{ color: '#1A73E8' }} /></Box>;

  return (
    <Box>
      {fc ? (
        <Box className={classes.greetWrap}>
          <Box className={classes.greetPill}><span className={classes.greetDot} />Backoffice ativo</Box>
          <Typography variant="h1" className={classes.greetTitle}>{greet}, <span className={classes.greetName}>{firstName}</span>.</Typography>
          <Typography className={classes.greetSub}>
            Tens <span className={classes.greetStrong}>{counts.orgs} organizações</span>, <span className={classes.greetStrong}>{counts.users} utilizadores</span> e <span className={classes.greetStrong}>{counts.profiles} perfis</span> sob gestão. Escolhe um cartão para abrir.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.header}>
          <Box className={classes.headerIcon}><DashboardIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 24 }} /></Box>
          <Box>
            <Typography className={classes.title}>Dashboard</Typography>
            <Typography className={classes.subtitle}>Visão geral do backoffice · organizações, perfis, utilizadores e zonas</Typography>
          </Box>
        </Box>
      )}

      {/* KPI cards */}
      <Box className={classes.grid}>
        {KPIS.map((k, i) => (
          <Box key={k.key} className={classes.kpi} style={{ animationDelay: `${i * 0.05}s` }} onClick={() => go(k.page)}>
            <Box className={classes.kpiTop}>
              <Box className={classes.kpiIcon} style={{ background: dark ? 'rgba(146,175,205,0.12)' : k.tint }}>
                <k.icon style={{ color: k.color, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography className={classes.kpiLabel}>{k.label}</Typography>
                <Typography className={classes.kpiNum} style={{ textAlign: 'right' }}>{counts[k.key]}</Typography>
              </Box>
            </Box>
            <Typography className={classes.kpiFoot}>{k.hint} <ArrowForwardIcon style={{ fontSize: 13 }} /></Typography>
          </Box>
        ))}
      </Box>

      {/* Cartão compacto — abre o ecrã "Como funciona o modelo" em grande */}
      <Box className={classes.card} onClick={() => go('model')}>
        <Box className={classes.cardHead}>
          <Box style={{ width: 40, height: 40, borderRadius: 11, background: dark ? 'rgba(32,166,212,0.14)' : '#e8f3ee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TuneIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 20 }} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography className={classes.cardTitle}>Como funciona o modelo</Typography>
            <Typography className={classes.cardSub} style={{ marginBottom: 0 }}>As 6 peças e o sentido das ligações — clica para ver a explicação completa.</Typography>
          </Box>
          <Typography className={classes.cardOpen}>Ver em detalhe <ArrowForwardIcon style={{ fontSize: 15 }} /></Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default AdminDashboard;
